import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useEffect } from "react";
import { resetChatSession } from "./agent/chat-session";
import { ConversionModalProvider } from "./components/plus/conversion-modal-context";
import { expireSession, isLoggedIn, subscribeToAuthChanges } from "./lib/auth";
import { trackHistory } from "./lib/nav-back";
import { queryClient, setUnauthorizedHandler } from "./lib/query-client";
import { onboardingOptions } from "./queries/onboarding";
import { router } from "./router";

// Remembers the pathname at each history index, which is the only way to know
// where the back button would LAND — the router says whether it can go back and
// never where. A caderno's "voltar" needs that to refuse to leave the caderno;
// see lib/nav-back.ts. Registered here because this is where the router lives.
trackHistory(router);

// Any query/mutation that hits a 401 (expired session) ends the local
// session and sends the user to the login page, which brings them back
// here after logging in again. Registered rather than written into the client
// itself, so lib/query-client.ts need not know the router exists — see there.
setUnauthorizedHandler(() => {
  expireSession();
  queryClient.clear();
  router.navigate({
    to: "/login",
    search: { redirect: router.state.location.href },
    replace: true,
  });
});

/*
 * Start reading the walkthrough status the moment the app boots.
 *
 * The route guards (lib/onboarding-guard.ts) await it before committing a
 * navigation, and on the very first one it would otherwise be a round trip in
 * FRONT of the screen's own data — the app's first paint waiting on a flag.
 * Started here it is in flight while the router resolves its first lazy chunk,
 * so the guard usually finds it already done and costs nothing.
 *
 * prefetchQuery, not ensureQueryData: it never throws, and nothing here waits
 * on it. Skipped when logged out, where every request is a 401 anyway.
 */
if (isLoggedIn()) {
  void queryClient.prefetchQuery(onboardingOptions());
}

// Login/logout elsewhere on the page (navbar) must not leave the previous
// user's data in the cache, and must re-run the route guards (beforeLoad)
function useAuthCacheSync() {
  useEffect(
    () =>
      subscribeToAuthChanges(() => {
        if (isLoggedIn()) {
          queryClient.invalidateQueries();
        } else {
          queryClient.clear();
          // The chat is kept outside React and mirrored to sessionStorage, so
          // without this the next student to log in on this tab would land in
          // the previous one's conversation
          resetChatSession();
        }
        router.invalidate();
      }),
    []
  );
}

export default function App() {
  useAuthCacheSync();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Uma só modal de conversão para toda a app, por cima do router: é aberta
          dos dez banners e nenhum deles devia hospedar a sua. Ver
          components/plus/conversion-modal-context. */}
      <ConversionModalProvider>
        <RouterProvider router={router} />
      </ConversionModalProvider>
    </QueryClientProvider>
  );
}
